import {Action, getModule, Module, Mutation, VuexModule} from "vuex-module-decorators";
import store from "@/store";
import {CustomerState} from "@/interfaces/state";
import {Customer} from "@/interfaces/entity";
import {
    createCustomer, deleteCustomer,
    listCustomer,
    singleCustomer,
    updateCustomer
} from "@/modules/customers/services";
import {IdMapper} from "@/interfaces/state/base";
import {PaginationMeta} from "@/interfaces/common";
import {CustomerParams} from "@/modules/customers/params";

@Module({ dynamic: true, name: "customer", store })
class CustomerModule extends VuexModule implements CustomerState {
    data: IdMapper<Customer> = {};
    ids: string[] = [];
    meta: PaginationMeta = new PaginationMeta();
    params: CustomerParams = new CustomerParams();

    get customers() {
        return this.ids.map((id: string) => this.data[id]);
    }

    @Mutation
    private UPDATE_CUSTOMER_PARAMS(payload: any) {
        this.params = payload;
    }

    @Mutation
    private MAP_CUSTOMER(payload: any) {
        const idMapper: IdMapper<Customer> = {};
        this.meta = payload.meta;
        this.ids = payload.datas?.map((customer: Customer) => {
            idMapper[customer.rowGuid!] = {...customer, isDeleting: false};
            return customer.rowGuid;
        });
        this.data = idMapper;
    }

    @Mutation
    private ADD_CUSTOMER(payload: Customer) {
        if (!payload || this.ids.includes(payload.rowGuid) || this.data[payload.rowGuid]) return;
        this.ids = [ ...this.ids, payload.rowGuid ];
        this.data = { ...this.data, [payload.rowGuid]: {...payload, isDeleting: false} }
    }

    @Mutation
    private UPDATE_CUSTOMER(payload: Customer) {
        if (!this.data[payload.rowGuid] || !this.ids.includes(payload.rowGuid)) return;
        this.data[payload.rowGuid] = { ...payload, isDeleting: false }
    }

    @Mutation
    private DELETE_CUSTOMER(payload: Customer) {
        if (!this.data[payload.rowGuid] || !this.ids.includes(payload.rowGuid)) return;
        this.ids = this.ids.filter((id: string) => id !== payload.rowGuid);
        delete this.data[payload.rowGuid];
    }

    @Mutation
    private START_DELETE_CUSTOMER(id: string) {
        this.data[id].isDeleting = true;
    }

    @Mutation
    private END_DELETE_CUSTOMER(id: string) {
        this.data[id].isDeleting = false;
    }

    @Action
    public updateCustomerParams(payload: any){
        this.UPDATE_CUSTOMER_PARAMS(payload);
    }

    @Action({ rawError: true })
    public addCustomer(payload: Customer) {
        this.ADD_CUSTOMER(payload);
    }

    @Action({ rawError: true })
    public async createCustomer(form: any) {
        const { data } = await createCustomer(form);
        this.ADD_CUSTOMER(data.data);
    }

    @Action({ rawError: true })
    public async listCustomer(params: CustomerParams = new CustomerParams()) {
        const { data } = await listCustomer(params);
        this.MAP_CUSTOMER(data);
    }

    @Action({ rawError: true })
    public async singleCustomer(id: string) {
        const { data } = await singleCustomer(id);
        return data.data;
    }

    @Action({ rawError: true })
    public async updateCustomer(dataReq: any) {
        const { data } = await updateCustomer(dataReq.rowGuid, dataReq);
        this.UPDATE_CUSTOMER(data.data);
    }

    @Action({ rawError: true })
    public async deleteCustomer(id: string) {
        this.START_DELETE_CUSTOMER(id);
        const { data } = await deleteCustomer(id);
        this.END_DELETE_CUSTOMER(id);
        this.DELETE_CUSTOMER(data.data);
    }

    @Action({ rawError: true })
    public async existInListCustomer(payload: string) {
        if (!payload || this.ids.some(id => id === payload)) return;
        const { data } = await singleCustomer(payload);
        this.ADD_CUSTOMER(data.data);
    }
}

export const CustomerStore = getModule(CustomerModule);